import {
  DiscountValidationResponse,
  DiscountDetails,
} from "@/lib/types/discount";
import {
  FALLBACK_DISCOUNT_CODE_PRICE,
  FALLBACK_DISCOUNT_CODES,
} from "./common/constants";

/**
 * Validates a discount code using Shopify's API
 * @param discountCode - The discount code to validate
 * @returns Promise<DiscountValidationResponse> - Validation result with discount details
 */
export async function validateDiscountCode(
  discountCode: string,
  workflow: string
): Promise<DiscountValidationResponse> {
  try {
    const response = await fetch(
      `${process.env.WEBSITE_HOST}/api/rebrand/validate-discount`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.WEB_AUTH_TOKEN}`,
        },
        body: JSON.stringify({ discountCode, workflow }),
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: "Network error" }));

      return {
        valid: false,
        error: errorData.error || "Failed to validate discount code",
      };
    }

    const data: DiscountValidationResponse = await response.json();
    return { ...data, valid: true };
  } catch (error) {
    console.error("Discount validation error:", error);
    return {
      valid: false,
      error: "Network error occurred",
    };
  }
}

/**
 * Calculates the discount amount based on discount details and original price
 * @param discount - Discount details from validation
 * @param originalPrice - Original price before discount
 * @returns number - Discount amount
 */
export function calculateDiscountAmount(
  discount: DiscountDetails,
  originalPrice: number
): number {
  if (discount.value_type === "fixed_amount") {
    return Math.min(discount.value, originalPrice);
  } else if (discount.value_type === "percentage") {
    return Math.ceil((originalPrice * discount.value) / 100);
  }
  return 0;
}

/**
 * Calculates the final price after applying discount
 * @param discount - Discount details from validation
 * @param originalPrice - Original price before discount
 * @returns number - Final price after discount
 */
export function calculateFinalPrice(
  discount: DiscountDetails,
  originalPrice: number
): number {
  const discountAmount = calculateDiscountAmount(discount, originalPrice);
  return Math.max(0, originalPrice - discountAmount);
}

/**
 * Formats discount information for display
 * @param discount - Discount details from validation
 * @returns string - Formatted discount text
 */
export function formatDiscountText(discount: DiscountDetails): string {
  if (discount.value_type === "fixed_amount") {
    return `$${discount.value} off`;
  } else if (discount.value_type === "percentage") {
    return `${discount.value}% off`;
  }
  return "Discount applied";
}

/**
 * Checks if a discount code is currently active based on start/end dates
 * @param discount - Discount details from validation
 * @returns boolean - Whether the discount is currently active
 */
export function isDiscountActive(discount: DiscountDetails): boolean {
  const now = new Date();

  if (discount.starts_at) {
    const startsAt = new Date(discount.starts_at);
    if (now < startsAt) return false;
  }

  if (discount.ends_at) {
    const endsAt = new Date(discount.ends_at);
    if (now > endsAt) return false;
  }

  return true;
}

export function getFallbackDiscountCode(workflow: string) {
  return {
    code: FALLBACK_DISCOUNT_CODES[
      (workflow || "og") as keyof typeof FALLBACK_DISCOUNT_CODES
    ],
    value:
      FALLBACK_DISCOUNT_CODE_PRICE[
        (workflow || "og") as keyof typeof FALLBACK_DISCOUNT_CODE_PRICE
      ],
  };
}

export async function getProductInventoryStatus(productId: string) {
  try {
    const response = await fetch(
      `${process.env.WEBSITE_HOST}/api/rebrand/inventory/${productId}`,
      {
        headers: {
          Authorization: `Bearer ${process.env.WEB_AUTH_TOKEN}`,
        },
      }
    );
    const data = await response.json();
    return data.isOutOfStock;
  } catch (error) {
    return false;
  }
}
